import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from "react-redux";
import {compose} from "recompose";
import SalesSummaryReportCategoryTables from './SalesSummaryReportCategoryTables';
import SalesSummaryReportLocationTable from './SalesSummaryReportLocationTable';
import SalesSummaryReportProductTables from './SalesSummaryReportProductTables';
import {getGetHeaders} from '../shared/serverFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import {RESOURCES as reportsResources} from '../fuseSalesSummaryReportsResourceMap';
import Config from '../messages';

/**
 * Class for retrieving Sales Summary data for multiple tables
 *
 */
class SalesSummaryReportSet extends Component {
    constructor(props) {
        super(props);
        this.state = {
            categorySummariesComplete: false,
            dataIsFiltered: false,
            dataLocations: [],
            dataLocationsData: [],
            dataLocationsTotalsData: [],
            dataCategoryReturns: [],
            dataCategorySales: [],
            dataProductReturns: [],
            dataProductSales: [],
            dataProcessed: false,
            errorMessage: null,
            reportName: 'Sales Summary for Locations, Product Units and Categories',
            locationSummaryComplete: false,
            productSummariesComplete: false,
            summariesComplete: false,
        };
        this.checkSummaryCompletion = this.checkSummaryCompletion.bind(this);
    }

    componentWillMount() {
        this.fetchData('all');
        this.fetchTotalsData('all');
    }

    componentWillReceiveProps(nextProps) {
        let clearData = false;
        // If initial date range or filter choices change, reset
        if (
            nextProps.startDate !== this.props.startDate ||
            nextProps.endDate !== this.props.endDate ||
            nextProps.offset !== this.props.offset ||
            nextProps.filterChoiceFirst !== this.props.filterChoiceFirst ||
            nextProps.filterChoiceSecond !== this.props.filterChoiceSecond ||
            nextProps.filterType !== this.props.filterType
        ) {
            clearData = true;
        }
        if (clearData) {
            this.setState({
                dataIsFiltered: false,
                dataLocations: [],
                dataCategoryReturns: [],
                dataCategorySales: [],
                dataProductReturns: [],
                dataProductSales: [],
                errorMessage: null,
                dataProcessed: false,
                summariesComplete: false
            });
        }
    }

    componentDidUpdate(prevProps) {
        let dataTypeToFetch = 'none';
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.offset !== this.props.offset ||
            (prevProps.filterType !== 'all' && this.props.filterType === 'all')
        ) {
            dataTypeToFetch = 'all';
        }
        if (
            prevProps.filterChoiceFirst !== this.props.filterChoiceFirst ||
            prevProps.filterChoiceSecond !== this.props.filterChoiceSecond ||
            prevProps.totalFiltersAvailable !== this.props.totalFiltersAvailable
        ) {
            if (this.props.totalFiltersAvailable === 2) {
                dataTypeToFetch = (this.props.filterChoiceSecond) ? 'filter' : 'none';
            } else if (this.props.totalFiltersAvailable === 1) {
                dataTypeToFetch = 'filter';
            }
        }
        if (dataTypeToFetch === 'filter') {
            this.fetchData(prevProps.filterType);
            this.fetchTotalsData(prevProps.filterType);
        } else if (dataTypeToFetch === 'all') {
            this.fetchData('all');
            this.fetchTotalsData('all');
        } else {
            // Do nothing
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get filtered data by a specific date range
     *
     * @param dataType
     */
    fetchData(dataType) {

        // Assemble the URI
        const params = {
            start_date: this.props.startDate,
            end_date: this.props.endDate,
            offset: this.props.offset,
        };

        // Update parameters and set resource based on data filter
        let resource;

        let firstFilterValue, secondFilterValue;
        if (dataType !== 'all') {
            if (this.props.filterChoiceFirst) {
                firstFilterValue = this.props.filterChoiceFirst;
            }
            if (this.props.filterChoiceSecond) {
                secondFilterValue = this.props.filterChoiceSecond;
            }
        }
        let dataIsFiltered = (dataType !== 'all');

        // Do not continue if filter choices are not available due to Primary/Secondary toggling
        if (dataType !== 'all' && !firstFilterValue) {
            return;
        }

        // Set additional params and select API path based on data type
        switch (dataType) {
            case 'statecity':
                params.state = firstFilterValue;
                params.city = secondFilterValue;
                resource = reportsResources.SalesSummaryByStateCity.GET_LIST(params);
                break;
            case 'statedba':
                params.state = firstFilterValue;
                params.dba_id = secondFilterValue;
                resource = reportsResources.SalesSummaryByStateDba.GET_LIST(params);
                break;
            case 'dba':
                params.dba_id = firstFilterValue;
                resource = reportsResources.SalesSummaryByDba.GET_LIST(params);
                break;
            case 'district':
                params.district_id = firstFilterValue;
                resource = reportsResources.SalesSummaryByDistrict.GET_LIST(params);
                break;
            case 'location':
                params.location_id = firstFilterValue;
                resource = reportsResources.SalesSummaryByLocation.GET_LIST(params);
                break;
            case 'state':
                params.state = firstFilterValue;
                resource = reportsResources.SalesSummaryByState.GET_LIST(params);
                break;
            case 'all':
                resource = reportsResources.SalesSummaryAll.GET_LIST(params);
                break;
            default:
                // Get all data
                resource = reportsResources.SalesSummaryAll.GET_LIST(params);
                break;
        }

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(dataIsFiltered, result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                dataLocations: [],
                dataCategoryReturns: [],
                dataCategorySales: [],
                dataProductReturns: [],
                dataProductSales: [],
                errorMessage: message,
                summariesComplete: true
            }, () => {
                this.props.onDataProcessed('reports');
                this.props.onSummariesComplete('reports', true);
            });
        });
    }

    fetchTotalsData(dataType) {

        // Assemble the URI
        const params = {
            start_date: this.props.startDate,
            end_date: this.props.endDate,
            offset: this.props.offset,
        };

        // Update parameters and set resource based on data filter
        let resource;

        let firstFilterValue, secondFilterValue;
        if (dataType !== 'all') {
            if (this.props.filterChoiceFirst) {
                firstFilterValue = this.props.filterChoiceFirst;
            }
            if (this.props.filterChoiceSecond) {
                secondFilterValue = this.props.filterChoiceSecond;
            }
        }
        let dataIsFiltered = (dataType !== 'all');

        // Do not continue if filter choices are not available due to Primary/Secondary toggling
        if (dataType !== 'all' && !firstFilterValue) {
            return;
        }

        // Set additional params and select API path based on data type
        switch (dataType) {
            case 'statecity':
                params.state = firstFilterValue;
                params.city = secondFilterValue;
                resource = reportsResources.SalesSummaryTotalsByStateCity.GET_LIST(params);
                break;
            case 'statedba':
                params.state = firstFilterValue;
                params.dba_id = secondFilterValue;
                resource = reportsResources.SalesSummaryTotalsByStateDba.GET_LIST(params);
                break;
            case 'dba':
                params.dba_id = firstFilterValue;
                resource = reportsResources.SalesSummaryTotalsByDba.GET_LIST(params);
                break;
            case 'district':
                params.district_id = firstFilterValue;
                resource = reportsResources.SalesSummaryTotalsByDistrict.GET_LIST(params);
                break;
            case 'location':
                params.location_id = firstFilterValue;
                resource = reportsResources.SalesSummaryTotalsByLocation.GET_LIST(params);
                break;
            case 'state':
                params.state = firstFilterValue;
                resource = reportsResources.SalesSummaryTotalsByState.GET_LIST(params);
                break;
            case 'all':
                resource = reportsResources.SalesSummaryAllTotals.GET_LIST(params);
                break;
            default:
                // Get all data
                resource = reportsResources.SalesSummaryAllTotals.GET_LIST(params);
                break;
        }

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processTotalsData(dataIsFiltered, result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                dataLocations: [],
                dataCategoryReturns: [],
                dataCategorySales: [],
                dataProductReturns: [],
                dataProductSales: [],
                errorMessage: message,
                summariesComplete: true
            }, () => {
                this.props.onDataProcessed('reports');
                this.props.onSummariesComplete('reports', true);
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store results
     *
     * @param dataIsFiltered
     * @param data
     */
    processData(dataIsFiltered, data) {
        let dataLocations = [];
        let dataCategoryReturns = [];
        let dataCategorySales = [];
        let dataProductReturns = [];
        let dataProductSales = [];

        if (data.aggregations) {

            // Cycle through data by Location and populate Location data
            if (data.aggregations.by_location && data.aggregations.by_location.buckets) {
                dataLocations = data.aggregations.by_location.buckets;
            }

            // Cycle through Product Unit Returns by Category and populate Category Returns dataset
            if (data.aggregations.product_unit_returns && data.aggregations.product_unit_returns.by_category && data.aggregations.product_unit_returns.by_category.buckets) {
                dataCategoryReturns = data.aggregations.product_unit_returns.by_category.buckets;
            }

            // Cycle through Product Unit Returns by Product and populate Product Unit Returns dataset
            if (data.aggregations.product_unit_returns && data.aggregations.product_unit_returns.by_product_unit && data.aggregations.product_unit_returns.by_product_unit.buckets) {
                dataProductReturns = data.aggregations.product_unit_returns.by_product_unit.buckets;
            }

            // Cycle through Product Unit Sales by Category and populate Category Sales dataset
            if (data.aggregations.product_unit_sales && data.aggregations.product_unit_sales.by_category && data.aggregations.product_unit_sales.by_category.buckets) {
                dataCategorySales = data.aggregations.product_unit_sales.by_category.buckets;
            }

            // Cycle through Product Unit Sales by Product and populate Product Unit Sales dataset
            if (data.aggregations.product_unit_sales && data.aggregations.product_unit_sales.by_product_unit && data.aggregations.product_unit_sales.by_product_unit.buckets) {
                dataProductSales = data.aggregations.product_unit_sales.by_product_unit.buckets;
            }
        }

        // Finalize dataset
        this.setState({
            dataIsFiltered: dataIsFiltered,
            dataLocationsData: dataLocations,
            dataCategoryReturns: dataCategoryReturns,
            dataCategorySales: dataCategorySales,
            dataProductReturns: dataProductReturns,
            dataProductSales: dataProductSales,
            errorMessage: null
        }, () => {
            this.completeLocationData();
        });
    }

    processTotalsData(dataIsFiltered, data) {
        let dataLocations = [];

        if (data.aggregations) {
            // Cycle through data by Location and populate Location data
            if (data.aggregations.by_location && data.aggregations.by_location.buckets) {
                dataLocations = data.aggregations.by_location.buckets;
            }
        }
        // Finalize dataset
        this.setState({
            dataIsFiltered: dataIsFiltered,
            dataLocationsTotalsData: dataLocations,
        }, () => {
            this.completeLocationData();
        })
    }

    completeLocationData() {
        let locations = this.state.dataLocationsData;
        let locationsTotals = this.state.dataLocationsTotalsData;

        if (locations.length > 0 && locationsTotals.length > 0) {

            for (let i = 0; i < locations.length; i++) {
                let result = locations[i];
                let location = result.column_name.hits.hits[0]._source.rxtxMsg.payload.location;

                // Check if this Location ID already exists in the table
                const idx = locationsTotals.findIndex(r => r.column_name.hits.hits[0]._source.rxtxMsg.payload.location.id.toString() === location.id.toString());
                if (idx !== -1) {
                    let totalsResult = locationsTotals[idx];
                    let countCredit = 0;
                    let totalCredit = 0;
                    let countCash = 0;
                    let totalCash = 0;
                    let totalReturns = 0;
                    let totalSales = 0;
                    let totalNet = 0;

                    if (totalsResult.cash_filter) {
                        if (totalsResult.cash_filter.cash_count) {
                            countCash = totalsResult.cash_filter.cash_count.value;
                            locations[i].payments.cash_filter.cash_count = {value: countCash};
                        }
                        if (totalsResult.cash_filter.cash_total) {
                            totalCash = totalsResult.cash_filter.cash_total.value;
                            if (result.payments && result.payments.cash_filter) {
                                if (result.payments.cash_filter.cash_total) {
                                    locations[i].payments.cash_filter.cash_total.value = totalCash;
                                }
                            }
                        }
                    }

                    if (totalsResult.credit_card_filter) {
                        if (totalsResult.credit_card_filter.credit_card_count) {
                            countCredit = totalsResult.credit_card_filter.credit_card_count.value;
                            locations[i].payments.credit_card_filter.credit_card_count = {value: countCredit};
                        }
                        if (totalsResult.credit_card_filter.credit_card_total) {
                            totalCredit = totalsResult.credit_card_filter.credit_card_total.value;
                            if (result.payments && result.payments.credit_card_filter) {
                                if (result.payments.credit_card_filter.credit_card_total) {
                                    locations[i].payments.credit_card_filter.credit_card_total.value = totalCredit;
                                }
                            }
                        }
                    }

                    if (totalsResult.total_gross_returns && totalsResult.total_gross_returns.refund_total) {
                        totalReturns = totalsResult.total_gross_returns.refund_total.value;
                        if (result.credits && result.credits.cash_credit_filter && result.credits.cash_credit_filter.credit_total) {
                            locations[i].credits.cash_credit_filter.credit_total.value = totalReturns;
                        }
                    }

                    if (totalsResult.total_gross_sales && totalsResult.total_gross_sales.gross_total) {
                        totalSales = totalsResult.total_gross_sales.gross_total.value;
                        if (result.payments && result.payments.cash_credit_filter && result.payments.cash_credit_filter.payment_total) {
                            locations[i].payments.cash_credit_filter.payment_total.value = totalSales;
                        }
                    }

                    if (totalsResult.total_net && totalsResult.total_net.net_total) {
                        totalNet = totalsResult.total_net.net_total.value;
                    }
                    locations[i].total_net = {
                        value: totalNet
                    };
                }
            }
        }
        // Finalize dataset
        this.setState({
            dataLocations: locations,
            dataProcessed: true,
        }, () => {
            this.props.onDataProcessed('reports');
        });
    }

    /* Event Handling ------------------------------ */

    // Handle event from table components
    handleSummaryComplete = (summary, isComplete) => {
        switch (summary) {
            case 'category':
                this.setState({
                    categorySummariesComplete: isComplete,
                }, () => {
                    this.checkSummaryCompletion();
                });
                break;
            case 'location':
                this.setState({
                    locationSummaryComplete: isComplete,
                }, () => {
                    this.checkSummaryCompletion();
                });
                break;
            case 'product':
                this.setState({
                    productSummariesComplete: isComplete,
                }, () => {
                    this.checkSummaryCompletion();
                });
                break;
            default:
                break;
        }
    };

    // Determine if all summaries are complete
    checkSummaryCompletion = () => {
        const {
            categorySummariesComplete,
            locationSummaryComplete,
            productSummariesComplete
        } = this.state;
        if (categorySummariesComplete && locationSummaryComplete && productSummariesComplete) {
            this.props.onSummariesComplete('reports', true);
        }
    };

    /* Table Collection Display ------------------------------ */

    render() {

        const {
            endDate,
            startDate,
            system,
        } = this.props;

        const {
            dataIsFiltered,
            dataLocations,
            dataCategoryReturns,
            dataCategorySales,
            dataProductReturns,
            dataProductSales,
            dataProcessed,
            errorMessage
        } = this.state;

        return (
            <div>
                {dataProcessed &&
                <div>

                    {/* Table: Sales Summary by Location */}
                    {!errorMessage && dataLocations &&
                    <SalesSummaryReportLocationTable
                        listData={dataLocations}
                        dataIsFiltered={dataIsFiltered}
                        endDate={endDate}
                        startDate={startDate}
                        system={system}
                        onSummaryComplete={this.handleSummaryComplete}
                    />
                    }

                    {/* Tables: Top 10 Products Summaries */}
                    {!errorMessage && dataProductReturns && dataProductSales &&
                    <SalesSummaryReportProductTables
                        listDataReturns={dataProductReturns}
                        listDataSales={dataProductSales}
                        dataIsFiltered={dataIsFiltered}
                        endDate={endDate}
                        startDate={startDate}
                        system={system}
                        onSummariesComplete={this.handleSummaryComplete}
                    />
                    }

                    {/* Tables: Top 10 Categories Summaries */}
                    {!errorMessage && dataCategoryReturns && dataCategorySales &&
                    <SalesSummaryReportCategoryTables
                        listDataReturns={dataCategoryReturns}
                        listDataSales={dataCategorySales}
                        dataIsFiltered={dataIsFiltered}
                        endDate={endDate}
                        startDate={startDate}
                        system={system}
                        onSummariesComplete={this.handleSummaryComplete}
                    />
                    }

                </div>
                }

                {/* Error and Messaging */}
                {errorMessage &&
                    <div className="error">
                        <p>{errorMessage}</p>
                    </div>
                }
            </div>
        )
    }
}

SalesSummaryReportSet.propTypes = {
    addField: PropTypes.bool,
    endDate: PropTypes.string,
    filterChoiceFirst: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.object,
        PropTypes.string,
    ]),
    filterChoiceSecond: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.object,
        PropTypes.string,
    ]),
    filterType: PropTypes.string,
    offset: PropTypes.string.isRequired,
    onDataProcessed: PropTypes.func,
    onSummariesComplete: PropTypes.func,
    startDate: PropTypes.string,
    system: PropTypes.string,
    totalFiltersAvailable: PropTypes.number,
};

SalesSummaryReportSet.defaultProps = {
    addField: true,
    endDate: null,
    filterChoiceFirst: null,
    filterChoiceSecond: null,
    filterType: 'all',
    offset: null,
    startDate: null,
    totalFiltersAvailable: 0,
};

const enhance = compose(
    connect()
);

export default enhance(SalesSummaryReportSet);



// WEBPACK FOOTER //
// ./src/Fuse/components/SalesSummaryReportSet.js